import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import { queryClient } from '../lib/queryClient';
import BottomNavigation from '../components/Navigation/BottomNavigation';
import { AppReadyGate } from '../components/AppReadyGate/AppReadyGate';
import { trackPageView, initializeAnalytics } from '../lib/analytics';
import { sanitizePagePath } from '../lib/analyticsUrl';

// ページコンポーネント
import {
  HomePage,
  HealthGraphPage,
  MenuPage,
  UserProfilePage,
  FamilyListPage,
  FamilyDetailPage,
  LinkTokenPage,
  ConsentPage,
  MessagePage,
  AiChatTopPage,
  AiChatConversationPage,
} from '../pages';

// アプリ起動時にGA4を初期化（同意に関係なくページビューをトラッキング）
initializeAnalytics();

/**
 * メインレイアウト - ボトムナビゲーション付き
 */
const MainLayout: React.FC = () => {
  return (
    <>
      <Outlet />
      <BottomNavigation />
    </>
  );
};

/**
 * シンプルレイアウト - ボトムナビゲーションなし
 */
const SimpleLayout: React.FC = () => {
  return <Outlet />;
};

/**
 * ページビュートラッキングコンポーネント
 */
const PageViewTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // ルート変更時にページビューを送信。遷移用のパラメータ（patient_id / thread_id /
    // message_id / date）を計測に載せないため、許可リストで洗ってから渡す
    // （docs/integrations/ga4.md「クエリ文字列の扱い」）
    trackPageView(sanitizePagePath(location.pathname, location.search));
  }, [location]);

  return null;
};

/**
 * AppRouter
 *
 * 認証・プロフィール・利用者リストの 3 段階チェックは AppReadyGate に統合。
 * 各段階は同一の <LoadingSpinner fullPage message="読み込み中..." /> を返すため、
 * ステップが進んでも DOM は同じスピナーのまま遷移し、点滅しない。
 */
export const AppRouter: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          {/* 招待リンクページ (認証不要) */}
          <Route path="/link_token" element={<LinkTokenPage />} />

          {/* 通信の秘密同意ページ (認証必須・同意ゲートは通さない)
              LINE アカウント単位で同意を保存するため line_user_id が必要 */}
          <Route
            element={
              <AppReadyGate mode="authOnlySkipConsent">
                <Outlet />
              </AppReadyGate>
            }
          >
            <Route path="/consent" element={<ConsentPage />} />
          </Route>

          {/* 認証のみ必要（プロフィール完了は問わない）ルート */}
          <Route
            element={
              <AppReadyGate mode="authOnly">
                <Outlet />
              </AppReadyGate>
            }
          >
            {/* プロフィール登録ページ */}
            <Route path="menu/profile" element={<UserProfilePage />} />
          </Route>

          {/* 認証 + プロフィール完了 + 利用者リスト取得済みのルート */}
          <Route
            element={
              <AppReadyGate mode="requireProfile">
                <Outlet />
              </AppReadyGate>
            }
          >
            {/* メインレイアウト (ボトムナビゲーション付き) */}
            <Route element={<MainLayout />}>
              <Route index element={<HomePage />} />
              <Route path="health-graph" element={<HealthGraphPage />} />
              <Route path="settings" element={<MenuPage />} />
              <Route path="message" element={<MessagePage />} />
              <Route path="ai-chat" element={<AiChatTopPage />} />
            </Route>

            {/* AI相談室 会話画面 (ボトムナビゲーションなし) */}
            <Route element={<SimpleLayout />}>
              <Route path="ai-chat/conversation" element={<AiChatConversationPage />} />
              <Route path="ai-chat/conversation/:conversationId" element={<AiChatConversationPage />} />
            </Route>

            {/* メニュー配下サブページ (ボトムナビゲーションなし) */}
            <Route path="menu" element={<SimpleLayout />}>
              <Route index element={<MenuPage />} />
              <Route path="families" element={<FamilyListPage />} />
              <Route path="families/:lineUserId/:patientId" element={<FamilyDetailPage />} />
            </Route>
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default AppRouter;
